import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { MainContext } from "./Main";
import {
  getMondayBoardColumns,
  getMondayBoardsForEditor,
  getMondayColumnList,
  getProfile,
  postAddMondayColumn,
  postRemoveMondayColumn,
} from "../utils/api";
import { IMondayBoard, IMondayColumn, IProfile } from "../interfaces";

const MISSING_COLUMN_TOOLTIP =
  "This column no longer exists on the monday.com board, or your authorized monday.com user no longer has access to it. Remove it from this list or restore it in monday.com.";

const MondayColumnEditor = () => {
  const context = useContext(MainContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [searchingBoards, setSearchingBoards] = useState<boolean>(false);
  const [loadingColumns, setLoadingColumns] = useState<boolean>(false);
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [snackbarContent, setSnackbarContent] = useState<string>("");
  const [selectedColumns, setSelectedColumns] = useState<IMondayColumn[]>([]);
  const [boardSearchResults, setBoardSearchResults] = useState<IMondayBoard[]>(
    [],
  );
  const [boardColumns, setBoardColumns] = useState<IMondayColumn[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<IMondayBoard | null>(null);
  const [boardSearchQuery, setBoardSearchQuery] = useState<string>("");
  const [profileId, setProfileId] = useState<string | null>(
    useParams().profileId || null,
  );
  const [profile, setProfile] = useState<IProfile | null>(null);

  useEffect(() => {
    const getColumnListAsync = async (localProfileId: string) => {
      const columnListResponse = await getMondayColumnList(localProfileId);
      setSelectedColumns(columnListResponse);
      setLoading(false);
    };

    const getProfileAsync = async (localProfileId: string) => {
      const profileResponse = await getProfile(localProfileId);
      setProfile(profileResponse.profile);
    };

    if (profileId === null) {
      if (context.user) {
        setProfileId(context.user.profileId);
        getColumnListAsync(context.user.profileId);
      } else {
        navigate("/login");
      }
    } else {
      getProfileAsync(profileId);
      getColumnListAsync(profileId);
    }
  }, [context.user, navigate, profileId]);

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackOpen(false);
  };

  const showSnackbar = (message: string) => {
    setSnackbarContent(message);
    setSnackOpen(true);
  };

  const columnKey = (boardId: string, columnId: string) =>
    `${boardId}:${columnId}`;

  const isColumnSelected = (boardId: string, columnId: string) =>
    selectedColumns.some(
      (column) =>
        column.boardId === boardId && column.columnId === columnId,
    );

  const handleRemoveColumn = async (column: IMondayColumn) => {
    const key = columnKey(column.boardId, column.columnId);
    setRemovingKey(key);
    try {
      await postRemoveMondayColumn(
        profileId as string,
        column.boardId,
        column.columnId,
      );
      setSelectedColumns((prev) =>
        prev.filter(
          (c) =>
            !(
              c.boardId === column.boardId && c.columnId === column.columnId
            ),
        ),
      );
    } catch (e) {
      console.error(e);
      showSnackbar("Error removing column");
    }
    setRemovingKey(null);
  };

  const handleAddColumn = async (column: IMondayColumn) => {
    if (
      !column.columnId ||
      !column.name ||
      !selectedBoard ||
      isColumnSelected(selectedBoard.boardId, column.columnId)
    ) {
      return;
    }

    const key = columnKey(selectedBoard.boardId, column.columnId);
    setAddingKey(key);
    try {
      const addedColumn = await postAddMondayColumn(profileId as string, {
        boardId: selectedBoard.boardId,
        boardName: selectedBoard.name,
        columnId: column.columnId,
        name: column.name,
      });
      setSelectedColumns((prev) => [...prev, addedColumn]);
      showSnackbar("Column added");
    } catch (e) {
      console.error(e);
      showSnackbar("Error adding column");
    }
    setAddingKey(null);
  };

  const handleBoardSearch = async () => {
    const trimmedQuery = boardSearchQuery.trim();
    if (!trimmedQuery) {
      setBoardSearchResults([]);
      showSnackbar("Enter a board name or ID to search");
      return;
    }

    setSearchingBoards(true);
    try {
      const results = await getMondayBoardsForEditor(
        trimmedQuery,
        profileId as string,
      );
      setBoardSearchResults(results);
    } catch (e) {
      console.error(e);
      setBoardSearchResults([]);
      showSnackbar("Error searching for boards");
    }
    setSearchingBoards(false);
  };

  const handleSelectBoard = async (board: IMondayBoard) => {
    setSelectedBoard(board);
    setBoardColumns([]);
    setLoadingColumns(true);
    try {
      const response = await getMondayBoardColumns(
        profileId as string,
        board.boardId,
      );
      setBoardColumns(response.columns);
      setSelectedBoard({
        boardId: response.boardId || board.boardId,
        name: response.boardName || board.name,
      });
    } catch (e) {
      console.error(e);
      setBoardColumns([]);
      showSnackbar("Error loading board columns");
    }
    setLoadingColumns(false);
  };

  return (
    <Styled>
      {!(
        profile?.mondayMembershipActive ||
        profile?.mondayFreeTrialActive ||
        context?.user?.mondayMembershipActive ||
        context?.user?.mondayFreeTrialActive
      ) ? (
        <>
          <Typography variant="h3" paragraph>
            This feature requires a paid plan or active free trial
          </Typography>

          <Typography variant="body1" paragraph>
            <Button
              variant="contained"
              href="https://monday.com/marketplace/listing/10000525/intercom#pricing"
              target="_blank"
              rel="noopener noreferrer"
            >
              Upgrade now
            </Button>
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h1">monday.com Column Editor</Typography>
          <Typography variant="body1" paragraph>
            Here you can choose which monday.com columns you can edit in
            Intercom when creating a task.
          </Typography>
          <Typography variant="body1" paragraph>
            Search for a board, select it, then add columns to your Selected
            Columns list. When you create a task on that board in Intercom,
            those columns will appear on the create form. More info in{" "}
            <a
              href="https://thackd.com/monday-in-the-inbox#select-columns"
              target="_blank"
              rel="noopener noreferrer"
            >
              our docs
            </a>
            .
          </Typography>

          {loading ? (
            <div className="loading-container">
              <CircularProgress />
            </div>
          ) : (
            <div className="content">
              <div className="search-container">
                <Typography variant="h3">1. Select a Board</Typography>
                <TextField
                  label="Board name or ID"
                  value={boardSearchQuery}
                  onChange={(e) => setBoardSearchQuery(e.target.value)}
                />
                <LoadingButton
                  loading={searchingBoards}
                  variant="contained"
                  onClick={handleBoardSearch}
                  disabled={!boardSearchQuery.trim()}
                  className="search-button"
                >
                  Search boards
                </LoadingButton>
                <div className="search-results">
                  <Typography variant="h4" className="search-results-title">
                    Boards
                  </Typography>
                  {boardSearchResults.length === 0 ? (
                    <Typography variant="body1">No boards found</Typography>
                  ) : (
                    <div className="field-list">
                      {boardSearchResults.map((board) => (
                        <React.Fragment key={board.boardId}>
                          <Typography variant="body1" className="field-name">
                            {board.name}
                          </Typography>
                          <Button
                            variant={
                              selectedBoard?.boardId === board.boardId
                                ? "outlined"
                                : "contained"
                            }
                            onClick={() => handleSelectBoard(board)}
                          >
                            {selectedBoard?.boardId === board.boardId
                              ? "Selected"
                              : "Select"}
                          </Button>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>

                {selectedBoard && (
                  <div className="columns-section">
                    <Typography variant="h3">
                      2. Add Columns from {selectedBoard.name}
                    </Typography>
                    {loadingColumns ? (
                      <CircularProgress size={28} />
                    ) : boardColumns.length === 0 ? (
                      <Typography variant="body1">
                        No populate-able columns found on this board.
                      </Typography>
                    ) : (
                      <div className="field-list">
                        {boardColumns.map((column) => {
                          const key = columnKey(
                            selectedBoard.boardId,
                            column.columnId,
                          );
                          const selected = isColumnSelected(
                            selectedBoard.boardId,
                            column.columnId,
                          );
                          return (
                            <React.Fragment key={key}>
                              <Typography
                                variant="body1"
                                className="field-name"
                              >
                                {column.name}
                                <span className="column-type">
                                  {" "}
                                  ({column.type})
                                </span>
                              </Typography>
                              <LoadingButton
                                loading={addingKey === key}
                                variant="contained"
                                onClick={() => handleAddColumn(column)}
                                disabled={selected}
                              >
                                {selected ? "Added" : "Add"}
                              </LoadingButton>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="selected-fields-container">
                <Typography variant="h3">Selected Columns</Typography>
                {selectedColumns.length === 0 ? (
                  <Typography variant="body1">
                    No columns selected.
                  </Typography>
                ) : (
                  <div className="field-list">
                    {selectedColumns.map((column) => {
                      const key = columnKey(column.boardId, column.columnId);
                      return (
                        <React.Fragment key={key}>
                          <div className="field-name-row">
                            <div className="field-name-block">
                              <Typography
                                variant="body2"
                                className="board-label"
                              >
                                {column.boardName}
                              </Typography>
                              <Typography
                                variant="body1"
                                className="field-name"
                              >
                                {column.name}
                              </Typography>
                            </div>
                            {column.existsOnBoard === false && (
                              <Tooltip title={MISSING_COLUMN_TOOLTIP}>
                                <WarningAmberIcon
                                  className="warning-icon"
                                  fontSize="small"
                                  color="warning"
                                />
                              </Tooltip>
                            )}
                          </div>
                          <LoadingButton
                            loading={removingKey === key}
                            variant="contained"
                            onClick={() => handleRemoveColumn(column)}
                          >
                            Remove
                          </LoadingButton>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          <Snackbar
            open={snackOpen}
            autoHideDuration={3000}
            onClose={handleClose}
            message={snackbarContent}
          />
        </>
      )}
    </Styled>
  );
};

const Styled = styled.div`
  .MuiTypography-h1 {
    margin-bottom: 1rem;
  }

  .content {
    display: flex;
    align-items: flex-start;
    width: 100%;
    margin-top: 2rem;

    .MuiTypography-h3 {
      margin-bottom: 1rem;
    }

    .MuiTypography-body1 {
      margin-bottom: 0.3rem;
    }

    .selected-fields-container {
      margin-left: 6rem;
    }

    .field-list {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 0.5rem 1rem;
      align-items: center;
      max-width: 520px;
    }

    .field-name-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
    }

    .field-name-block {
      min-width: 0;
    }

    .board-label {
      color: #666;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .field-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 0;
    }

    .column-type {
      color: #888;
      font-size: 0.85em;
    }

    .warning-icon {
      flex-shrink: 0;
      cursor: help;
    }

    .search-container {
      display: flex;
      flex-direction: column;
      margin-left: 2rem;

      .search-button {
        margin-top: 1rem;
        margin-bottom: 2rem;
      }

      .search-results {
        margin-top: 1rem;

        .search-results-title {
          margin-bottom: 1rem;
        }
      }

      .columns-section {
        margin-top: 2.5rem;
      }
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    width: 500px;
    max-width: 100%;
    margin-top: 2rem;
  }
`;

export default MondayColumnEditor;
